import {
  legacyPresetIdFromRegistrySlug,
  normalizeScenarioId,
  parseRegistryScenarioId,
} from './vectorScenarioRegistry';
import { registryScenarioUsesCompoundEquity } from './vectorBasketDisplay';

export const MONTHLY_RETURN_METHOD_IDS = ['fixed', 'compound'];

export const MONTHLY_RETURN_METHODS = {
  fixed: {
    id: 'fixed',
    label: 'Fixed $100k',
    tagline: 'Month P&L ÷ capital base. Months add to total return.',
  },
  compound: {
    id: 'compound',
    label: 'Account %',
    tagline: 'Month-end ÷ prior month-end. Matches how the account actually grew.',
  },
};

export const MONTHLY_METHOD_CATEGORY_ORDER = ['futures', 'stocks', 'equity_algos'];

export const MONTHLY_METHOD_CATEGORIES = {
  futures: {
    id: 'futures',
    label: 'Futures',
    hint: 'Master Multi Account, NQ journals, NT8, and test feeds. Fixed $100k is the usual fit — they trade a constant-size book.',
  },
  stocks: {
    id: 'stocks',
    label: 'Portfolio stocks',
    hint: 'Equity Portfolios, OG, Kairos, and other daily equity curves (e.g. Congress Trader). Account % matches the growth chart. Custom blends use this setting too.',
  },
  equity_algos: {
    id: 'equity_algos',
    label: 'Individual stock algos',
    hint: 'AAPL / NVDA / TSLA 1H algos and similar single-name sleeves. Usually sized on a flat $100k notional.',
  },
};

export const DEFAULT_MONTHLY_RETURN_METHODS = {
  futures: 'fixed',
  stocks: 'compound',
  equity_algos: 'fixed',
};

const STOCK_SLUGS = new Set([
  'equity_portfolio_1x',
  'equity_portfolio_225x',
  'og_equity_portfolio_1x',
  'og_equity_portfolio_225x',
  'vc_core_standard',
  'vc_core_aggressive',
  'kairos_standard',
  'kairos_aggressive',
]);

const FUTURES_SLUGS = new Set([
  'master_multi_account',
  'vector_capital_journal',
  'mma_test',
  'btc_test',
  'jean_test',
]);

export function normalizeMonthlyReturnMethod(raw, fallback = 'fixed') {
  const value = String(raw || '').trim().toLowerCase();
  return MONTHLY_RETURN_METHOD_IDS.includes(value) ? value : fallback;
}

export function normalizeMonthlyReturnMethods(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  return {
    futures: normalizeMonthlyReturnMethod(src.futures, DEFAULT_MONTHLY_RETURN_METHODS.futures),
    stocks: normalizeMonthlyReturnMethod(src.stocks, DEFAULT_MONTHLY_RETURN_METHODS.stocks),
    equity_algos: normalizeMonthlyReturnMethod(src.equity_algos, DEFAULT_MONTHLY_RETURN_METHODS.equity_algos),
  };
}

function scenarioSlugFromId(scenarioId) {
  const raw = String(scenarioId || '').trim();
  if (!raw) return '';
  const normalized = normalizeScenarioId(raw);
  return parseRegistryScenarioId(normalized)
    || legacyPresetIdFromRegistrySlug(raw)
    || parseRegistryScenarioId(raw)
    || raw.replace(/^scenario:/, '');
}

/** Which settings row applies to this scenario. Tests follow Futures. */
export function monthlyMethodCategoryForScenario(scenarioId, registryRow) {
  const registryCat = String(registryRow?.category || '').toLowerCase();
  if (registryCat === 'stocks' || registryCat === 'equity_algos' || registryCat === 'futures') {
    return registryCat;
  }
  if (registryCat === 'tests') {
    return registryScenarioUsesCompoundEquity(registryRow) ? 'stocks' : 'futures';
  }
  if (registryScenarioUsesCompoundEquity(registryRow)) return 'stocks';

  const slug = scenarioSlugFromId(scenarioId);
  if (STOCK_SLUGS.has(slug)) return 'stocks';
  if (FUTURES_SLUGS.has(slug)) return 'futures';

  const rawId = String(scenarioId || '');
  if (rawId.startsWith('dyn_eq:')) return 'equity_algos';
  if (rawId.startsWith('dyn_fut:')) return 'futures';

  if (String(scenarioId || '').trim() === 'custom') return 'stocks';
  return 'futures';
}

export function monthlyMethodForScenario(scenarioId, registryRow, methods) {
  const normalized = normalizeMonthlyReturnMethods(methods);
  const category = monthlyMethodCategoryForScenario(scenarioId, registryRow);
  return normalized[category] || DEFAULT_MONTHLY_RETURN_METHODS[category] || 'fixed';
}

/**
 * Capital base for calendar-month % — number for fixed-notional, null for
 * account-style month-over-month (buildMonthlyRows treats null as end÷start).
 */
export function monthlyFixedBaseCapital(scenarioId, registryRow, methods, capitalBase) {
  if (monthlyMethodForScenario(scenarioId, registryRow, methods) !== 'fixed') return null;
  const base = Number(capitalBase);
  return Number.isFinite(base) && base > 0 ? base : 100000;
}
